/* eslint-disable jsx-a11y/anchor-is-valid */
import Head from 'next/head';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import ProgressBanner from '@components/ProgressBanner';
import Container from '@components/Container';
import Section from '@components/Section';
import { useTranslation } from 'next-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCouponCode,
  setForceLogin,
  setIsBusiness,
  setIsSingle,
  setLoginAsExistingUser,
  setLpnAddedFromQuery,
  setTerminalSession,
  setAgreement,
} from '@features/Checkout/store';
import Button from '@components/Button';
import NoSsr from '@components/NoSsr/NoSsr';
import { pushStepToDataLayer } from '@utilities/helpers/common';
import { useGetMeQuery } from '@shared/services/users';
import useAuth from '@features/MyAccount/hooks/use-auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import Image from 'next/image';
import useWindowSize from '@hooks/use-window-size';
import { isMobileBelowWidth } from '@utilities/consts';
import ConditionalWrapper from '@components/ConditionalWrapper';
import {
  useGetProductsQuery,
  useGetSummariesMutation,
} from '@features/Checkout/services/checkout';
import * as stepComponents from './steps';
import {
  Root,
  DetailsStep,
  CheckoutSection,
  Container as ContainerClass,
  Header as HeaderClass,
  HeaderWrapper,
  CheckoutStep,
  SubHeaderText,
  SubHeaderTextList,
  SplashIconContainer,
  SplashIconText,
} from './CheckoutSteps.module.css';
import StartOverButton from '../StartOverButton';
import { useGateValue } from '@statsig/react-bindings';
import LoadingIcon from '@components/LoadingIcon';

const CheckoutSteps = () => {
  const { t } = useTranslation(['common', 'checkout', 'account']);
  const [washbackProduct, setWashbackProduct] = useState(null);
  const [isDeterminingCheckout, setIsDeterminingCheckout] = useState(true);
  const router = useRouter();
  const dispatch = useDispatch();
  const {
    step: stepFromQuery,
    c: couponCode,
    single: singleWash,
    business,
    s: terminalSession,
    lpn: licensePlate,
    // p: phoneNumber,
    a: membershipAgreement, // membershipAgreement uid
    forceLogin,
    newOrder = false,
    plan,
    atav,
    location: locationFromQuery,
  } = router.query;
  const {
    isBusiness,
    loginAsExistingUser,
    isSingle,
    terminalSession: isFromPos,
  } = useSelector((state) => state.checkout);
  const { options } = useSelector((state) => state.wordpress);
  const isSingleWash = Boolean(Number(singleWash));
  const isBusinessInQuery = Boolean(Number(business));
  const isForceLogin = Boolean(forceLogin);
  const { isAuthenticated: isAuth } = useAuth(isForceLogin);
  const { data: user } = useGetMeQuery(null, { skip: !isAuth });
  const windowSize = useWindowSize();
  const [, { data: summary }] = useGetSummariesMutation({
    fixedCacheKey: 'summary-shared',
  });
  const { data: products } = useGetProductsQuery(null);

  const isMobile = windowSize.width < isMobileBelowWidth;

  const isNewCheckout = useGateValue('new_checkout');

  useEffect(() => {
    if (isNewCheckout != null && isNewCheckout) {
      const query = {};

      if (singleWash) query.single = singleWash;
      if (business) query.business = business;
      if (terminalSession) query.s = terminalSession;
      if (licensePlate) query.lpn = licensePlate;
      if (membershipAgreement) query.a = membershipAgreement;
      if (plan) query.plan = plan;
      if (atav) query.atav = atav;
      if (couponCode) query.c = couponCode;
      if (locationFromQuery) query.location = locationFromQuery;

      router.push({
        pathname: '/checkout2',
        query: query,
      });
    } else {
      setIsDeterminingCheckout(false);
    }
  }, [isNewCheckout]);

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (!router.isReady || !user) return;
    if (user.vat) {
      dispatch(setIsBusiness(true));
    } else {
      dispatch(setIsBusiness(false));
    }
  }, [router.isReady, user, dispatch]);
  const steps = useMemo(() => {
    const headerMembership = () => {
      if ((isSingleWash && isBusiness) || (isSingle && isBusiness)) {
        return `${t('singleWash', { ns: 'common' })} ${t('businessMembership', {
          ns: 'common',
        })}`;
      }
      if (isSingleWash || isSingle) {
        return `${t('singleWash', { ns: 'common' })} ${t('membership', {
          ns: 'common',
        }).toLowerCase()}`;
      }
      if (isBusiness) {
        return t('businessMembership', { ns: 'common' });
      }
      return t('membershipHeader', { ns: 'checkout' });
    };
    const headerDetails = isBusiness
      ? t('CompanyInformation', { ns: 'account' })
      : t('yourInformation', { ns: 'account' });
    return [
      {
        number: 1,
        key: 'membership',
        name: t('membership', { ns: 'common' }),
        header: headerMembership(),
        component: stepComponents.Membership,
      },
      {
        number: 2,
        key: 'details',
        name: t('information', { ns: 'checkout' }),
        header: headerDetails,
        component: stepComponents.Information,
      },
      {
        number: 3,
        key: 'payment',
        name: t('payment', { ns: 'common' }),
        component: stepComponents.Payment,
      },
      {
        number: 4,
        key: 'confirmation',
        name: t('receipt', { ns: 'common' }),
        component: stepComponents.Confirmation,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, router.isReady, router.query, isBusiness, isSingle]);
  const currentStepFromRoute = useMemo(
    () => stepFromQuery && steps.find((s) => stepFromQuery.includes(s.key)),
    [stepFromQuery, steps]
  );
  const stepFromRouteOrFirst = currentStepFromRoute || steps[0];
  const [currentStep, setCurrentStep] = useState(stepFromRouteOrFirst);
  useEffect(() => pushStepToDataLayer(currentStep), [currentStep]);
  useEffect(() => setCurrentStep(stepFromRouteOrFirst), [stepFromRouteOrFirst]);
  /**
   * Handle coupon links checkout/info?p=61265478&c=syND2Tllbp&s=501b8267-4837-4c2e-a8eb-21c1a627c7d0
   */
  useEffect(() => {
    if (!router.isReady) return;
    if (
      stepFromQuery?.includes('info') ||
      couponCode ||
      terminalSession ||
      licensePlate ||
      membershipAgreement ||
      forceLogin
    ) {
      if (couponCode) {
        dispatch(setCouponCode(couponCode));
      }
      if (terminalSession) {
        dispatch(setTerminalSession(terminalSession));
      }
      if (licensePlate) {
        dispatch(setLpnAddedFromQuery(true));
      }
      if (forceLogin) {
        dispatch(setForceLogin(isForceLogin));
      }
      if (membershipAgreement) {
        dispatch(setAgreement(membershipAgreement));
      }
    }
    if (business) {
      dispatch(setIsBusiness(isBusinessInQuery));
    }
    if (singleWash) {
      dispatch(setIsSingle(isSingleWash));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);
  /**
   * Add bodyClass (for n-progress)
   */
  useEffect(() => {
    document.querySelector('body').classList.add('checkout');
    return () => {
      document.querySelector('body').classList.remove('checkout');
    };
  });
  /**
   * Go to previous step
   */
  const goToStep = (step) => {
    const newStep = steps?.[step];
    if (newStep) {
      router.push(
        {
          pathname: `/checkout/${newStep.key}`,
        },
        undefined,
        {
          shallow: true,
        }
      );
      setCurrentStep(newStep);
    }
  };
  /**
   * Go to next step
   */
  const nextStep = () => {
    goToStep(currentStep.number);
  };
  /**
   * Go to previous step
   */
  const previousStep = () => {
    goToStep(currentStep.number - 2);
  };
  /**
   * This is fired whenever a step has completed
   *
   * @param   {object}  data  The response from the completed step
   */
  const onStepCompleted = (/* data */) => {
    window.scrollTo({ top: 0 });
    nextStep();
  };
  /**
   * Fetch current step component
   */
  const currentStepComponent = (s) => {
    const CurrentStep = s.component;
    return (
      <CurrentStep
        onStepCompleted={onStepCompleted}
        previousStep={previousStep}
        goToStep={goToStep}
      />
    );
  };

  const isConfirmationPage = currentStep.key === 'confirmation';

  const classes = classNames(Root, {
    [DetailsStep]: currentStep.number === 1,
  });
  const loginHandler = (e) => {
    e.preventDefault();
    dispatch(setLoginAsExistingUser(true));
  };
  useEffect(() => {
    if (!summary || washbackProduct) return;
    const product = products?.find(
      // eslint-disable-next-line no-shadow
      (product) => product.name === summary?.total?.washbackName
    );
    setWashbackProduct(product);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, products]);
  const Splash = useMemo(
    () => (
      <div className={SplashIconContainer}>
        <div className={SplashIconText}>
          <p>{t('splash.0', { ns: 'checkout' })}</p>
          <p>
            {t('splash.1', {
              ns: 'checkout',
              amount: washbackProduct?.oneTimePrice / 100,
              currency: options?.currencySymbol,
            })}
          </p>
          <p>{t('splash.2', { ns: 'checkout' })}</p>
        </div>
        <Image
          alt="splash"
          src="/assets/icons/splash.svg"
          height={isMobile ? 83 : 130}
          width={isMobile ? 83 : 130}
          blurDataURL="blur"
          placeholder="blur"
          quality={50}
          priority
        />
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [washbackProduct]
  );

  const showProgressBanner = useCallback(() => {
    if (currentStep.number === 4) {
      return false;
    }
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newOrder]);

  if (isDeterminingCheckout) {
    return <LoadingIcon withWrapper noMaxWait size="large" />;
  }

  return (
    <>
      <Head>
        <title key="title">{`${currentStep.name} - Checkout`}</title>
      </Head>
      <div className={classes}>
        {showProgressBanner() && (
          <ProgressBanner
            steps={steps}
            currentStep={currentStep.number - 1}
            hasBackButton={[2, 3].includes(currentStep.number)}
            onGoBack={() => {
              if (currentStep.number - 1 !== 0) {
                previousStep();
              }
            }}
          />
        )}
        <Container className={ContainerClass}>
          {!isConfirmationPage && (
            <Section className={HeaderWrapper} hasSideBar={!isConfirmationPage}>
              <div className={CheckoutStep}>
                <NoSsr>
                  <div style={{ display: 'flex' }}>
                    <ConditionalWrapper
                      condition={!isMobile}
                      wrapper={(children) => (
                        <div
                          style={{ display: 'flex', flexDirection: 'column' }}
                        >
                          {children}
                        </div>
                      )}
                    >
                      <ConditionalWrapper
                        condition={isMobile}
                        wrapper={(children) => (
                          <div
                            style={{ display: 'flex', flexFlow: 'row wrap' }}
                          >
                            {children}
                          </div>
                        )}
                      >
                        <h1 className={HeaderClass}>{currentStep.header}</h1>
                        {isMobile &&
                          isFromPos &&
                          currentStep.number === 1 &&
                          washbackProduct && <>{Splash}</>}
                        {currentStep.number === 1 && !isSingle && (
                          <div>
                            {t('headerUSP', { ns: 'checkout' })}
                            <div>
                              <ul className={SubHeaderTextList}>
                                <li>
                                  <FontAwesomeIcon
                                    color="#06c167"
                                    icon={faCheck}
                                  />
                                  <span>{t('USP.0', { ns: 'checkout' })}</span>
                                </li>
                                <li>
                                  <FontAwesomeIcon
                                    color="#06c167"
                                    icon={faCheck}
                                  />
                                  <span>{t('USP.1', { ns: 'checkout' })}</span>
                                </li>
                                <li>
                                  <FontAwesomeIcon
                                    color="#06c167"
                                    icon={faCheck}
                                  />
                                  <span>{t('USP.2', { ns: 'checkout' })}</span>
                                </li>
                              </ul>
                            </div>
                          </div>
                        )}
                      </ConditionalWrapper>
                    </ConditionalWrapper>
                    {!isMobile &&
                      isFromPos &&
                      currentStep.number === 1 &&
                      washbackProduct && <>{Splash}</>}
                  </div>
                  <div className={SubHeaderText}>
                    {currentStep.number === 2 &&
                      !isAuth &&
                      !loginAsExistingUser && (
                        <div>
                          {isBusiness ? (
                            <div>
                              {t('privatePerson', { ns: 'checkout' })}?{' '}
                              <Button
                                appearance="text"
                                onClick={() => dispatch(setIsBusiness(false))}
                                size="extra-small"
                              >
                                {t('continueAsPrivate', { ns: 'checkout' })}
                              </Button>
                              <br />
                              {t('alreadyAMember', { ns: 'checkout' })}{' '}
                              <Button
                                appearance="text"
                                onClick={loginHandler}
                                size="extra-small"
                              >
                                {t('login', { ns: 'common' })}
                              </Button>
                            </div>
                          ) : (
                            <div>
                              {t('alreadyAMember', { ns: 'checkout' })}{' '}
                              <Button
                                appearance="text"
                                onClick={loginHandler}
                                size="extra-small"
                              >
                                {t('login', { ns: 'common' })}
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    {currentStep.number === 2 &&
                      !isAuth &&
                      loginAsExistingUser && (
                        <div>
                          <Button
                            appearance="text"
                            onClick={() =>
                              dispatch(setLoginAsExistingUser(false))
                            }
                            size="extra-small"
                          >
                            {`${t('createNewMember', { ns: 'checkout' })}?`}
                          </Button>
                        </div>
                      )}
                    {isSingle && currentStep.number === 1 && (
                      <div className={SubHeaderText}>
                        {t('singleWashInfo', { ns: 'checkout' })}
                      </div>
                    )}
                  </div>
                </NoSsr>
              </div>
            </Section>
          )}
          <Section className={CheckoutSection} hasSideBar={!isConfirmationPage}>
            {currentStepComponent(currentStep)}
          </Section>
          {(process.env.ENVIRONMENT === 'local' ||
            process.env.ENVIRONMENT === 'staging') &&
            currentStep.number === 1 && <StartOverButton />}
        </Container>
      </div>
    </>
  );
};

export default CheckoutSteps;
